import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import Spinner from 'components/UI/Spinner';
import { formatDate } from 'components/Forms/utils';
import styles from './ClientFormsTable.module.scss';
import FormsIsEmpty from 'components/Forms/Table/FormsIsEmpty';
import ClientFormOptions from './ClientFormOptions';
import { useTimeZone } from 'hooks';

export interface ClientFormRecord {
    id?: number | string;
    name?: string;
    title?: string;
    form?: { title?: string };
    sentAt?: string | Date | null;
    completedAt?: string | Date | null;
    dateSent?: string | Date | null;
    dateCompleted?: string | Date | null;
    channel?: string | null;
    status?: 'pending' | 'completed';
    [key: string]: any; // tolerate backend variations
}

interface ClientFormsTableProps {
    records: ClientFormRecord[];
    isLoading?: boolean;
    isEmpty?: boolean;
    // Current tag in parent view — controls which options to show per row
    status?: 'pending' | 'completed';
    pagination?: {
        page: number;
        totalPages: number;
        size?: number;
    };
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
    // Triggered from row menu on completed forms
    onView?: (record: ClientFormRecord) => void;
}

const ClientFormsTable: FunctionComponent<ClientFormsTableProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const colSpan = 5;
    const showHeader = !isMobile || (!props.isEmpty && !props.isLoading);
    const timeZone = useTimeZone();

    // Use the same date formatting used by Forms table for consistency

    const renderBody = () => {
        if (props.isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }
        if (props.isEmpty) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            className={styles.emptyCell}
                        >
                            <div className={styles.emptyCellContent}>
                                <FormsIsEmpty
                                    title="No forms yet"
                                    text="Add your first form to start..."
                                />
                            </div>
                        </td>
                    </tr>
                </TableBody>
            );
        }
        return (
            <TableBody>
                {props.records.map((r, idx) => {
                    const asAny = r as any;
                    const name =
                        r.form?.title ||
                        r.title ||
                        r.name ||
                        asAny.formTitle ||
                        (asAny.formId ? `Form #${asAny.formId}` : undefined) ||
                        '-';
                    // Prefer normalized fields coming from the container; avoid relying on raw keys
                    const sentFmt = formatDate(
                        (r as any).sentAt ?? (r as any).dateSent ?? (r as any).createdAt ?? undefined,
                        timeZone
                    );
                    const sent = sentFmt || '-';
                    const completedFmt = formatDate(
                        r.completedAt ?? r.dateCompleted ?? asAny.completed_at ?? undefined,
                        timeZone
                    );
                    const completed = completedFmt || '-';
                    const channelRaw = r.channel || (asAny.sent_via as string) || (asAny.sentVia as string) || '-';
                    let channel = '-';
                    if (channelRaw && channelRaw !== '-') {
                        const lower = channelRaw.toLowerCase();
                        channel = lower === 'sms' ? 'SMS' : lower.charAt(0).toUpperCase() + lower.slice(1);
                    }
                    return (
                        <TableRow key={String(r.id ?? idx)}>
                            <TableCell>
                                <p>{name}</p>
                            </TableCell>
                            <TableCell>
                                <p>{sent}</p>
                            </TableCell>
                            <TableCell>
                                <p>{completed}</p>
                            </TableCell>
                            <TableCell>
                                <p>{channel}</p>
                            </TableCell>
                            <TableCell isActions>
                                <ClientFormOptions
                                    status={props.status || 'pending'}
                                    record={r}
                                    onView={props.onView}
                                    lastRecord={idx === props.records.length - 1}
                                />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        );
    };

    return (
        <Table
            className={styles.table}
            fullHeight={true}
            pagination={props.pagination}
            onPageChange={props.onPageChange}
            onSizeChange={props.onSizeChange}
        >
            {showHeader && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Date Sent</TableHeaderCell>
                    <TableHeaderCell>Date Completed</TableHeaderCell>
                    <TableHeaderCell>Channel</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderBody()}
        </Table>
    );
};

export default ClientFormsTable;
